import { useState, useEffect, useRef, useCallback } from 'react'
import { useParams } from 'react-router-dom'
import QRCode from 'qrcode'
import { useEventStore } from '@/store'
import { Switch } from '@/admin/components/Switch'
import type { BoothOverrides } from '@/lib/boothOverrides'
import { Wifi, WifiOff, Play, Square, Settings, Grid, Check, RefreshCw, Sparkles, Upload, Trash2, ArrowUp, ArrowDown, Monitor } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { EventConfig } from '@/types'

export default function RemoteControl() {
  const { eventId } = useParams<{ eventId: string }>()
  const events = useEventStore((s) => s.events)
  const loadEvents = useEventStore((s) => s.loadEvents)
  const event = events.find((e) => e.id === eventId)

  useEffect(() => {
    loadEvents()
  }, [loadEvents])

  const [connected, setConnected] = useState(false)

  const [propsList, setPropsList] = useState<string[]>([])

  useEffect(() => {
    fetch('/api/props')
      .then(r => r.json())
      .then(data => setPropsList(data))
      .catch(err => console.error('Failed to fetch props:', err))
  }, [])
  const [globalPoses, setGlobalPoses] = useState<Array<{ filename: string; enabled: boolean }>>([])
  const [uploading, setUploading] = useState(false)
  const [selectedPoses, setSelectedPoses] = useState<string[]>([])
  const [qrCodeData, setQrCodeData] = useState<string>('')
  const [showSettings, setShowSettings] = useState(false)
  const [settings, setSettings] = useState<BoothOverrides>({})
  const wsRef = useRef<WebSocket | null>(null)
  const [activeBooths, setActiveBooths] = useState<Record<string, { screen: string; currentPose: number; mode: string; timestamp: number }>>({})

  useEffect(() => {
    if (event) {
      setSettings({
        beautyFilter: event.effects.beautyFilter.enabled,
        watermark: event.effects.watermark.enabled,
        greenScreen: event.effects.greenScreen.enabled,
        digitalProps: event.effects.digitalProps,
        poseSuggestions: event.capture.poseSuggestions?.enabled,
        poseSuggestionDelay: event.capture.poseSuggestions?.showSecondsBefore,
        poseCount: event.capture.photo.poseCount,
        countdownDelay: event.capture.photo.delayBeforeFirst,
        reviewTime: event.capture.photo.reviewTime,
        finishScreenTimeout: event.capture.finishScreenTimeout,
        qrEnabled: event.sharing.qrEnabled,
        debugOverlayResolution: false,
        recordSessionVideo: event.capture.recordSessionVideo,
        startButtonImageEnabled: event.screens.welcome.startButtonImage?.enabled,
        advancedBeautyFilter: event.effects.faceTracking?.enabled,
        faceTrackingProp: event.effects.faceTracking?.defaultProp,
        screensaverEnabled: event.capture.attractLoop?.enabled,
        maxMessages: event.capture.attractLoop?.maxMessages,
        intervalSeconds: event.capture.attractLoop?.intervalSeconds,
        motionSensitivity: event.capture.attractLoop?.motionSensitivity,
        wakeMethod: event.capture.attractLoop?.wakeMethod,
        zigbeeLightEnabled: event.capture.zigbee?.enabled,
      })
    }
  }, [event])

  const updateSetting = (key: keyof BoothOverrides, value: any) => {
    setSettings((prev) => ({ ...prev, [key]: value }))
    sendCommand('UPDATE_SETTINGS', { settings: { [key]: value } })
  }

  const resetToDefaults = () => {
    if (!event) return
    const defaults: BoothOverrides = {
      beautyFilter: event.effects.beautyFilter.enabled,
      watermark: event.effects.watermark.enabled,
      greenScreen: event.effects.greenScreen.enabled,
      digitalProps: event.effects.digitalProps,
      poseSuggestions: event.capture.poseSuggestions?.enabled,
      poseSuggestionDelay: event.capture.poseSuggestions?.showSecondsBefore,
      poseCount: event.capture.photo.poseCount,
      countdownDelay: event.capture.photo.delayBeforeFirst,
      reviewTime: event.capture.photo.reviewTime,
      finishScreenTimeout: event.capture.finishScreenTimeout,
      qrEnabled: event.sharing.qrEnabled,
      debugOverlayResolution: false,
      recordSessionVideo: event.capture.recordSessionVideo,
      startButtonImageEnabled: event.screens.welcome.startButtonImage?.enabled,
      advancedBeautyFilter: event.effects.faceTracking?.enabled,
      faceTrackingProp: event.effects.faceTracking?.defaultProp,
      maxMessages: event.capture.attractLoop?.maxMessages || 4,
      intervalSeconds: event.capture.attractLoop?.intervalSeconds || 2.5,
      motionSensitivity: event.capture.attractLoop?.motionSensitivity || 50,
      wakeMethod: event.capture.attractLoop?.wakeMethod || 'touch',
      zigbeeLightEnabled: event.capture.zigbee?.enabled,
    }
    setSettings(defaults)
    sendCommand('UPDATE_SETTINGS', { settings: defaults })
    alert('Settings reset to defaults!')
  }

  useEffect(() => {
    const protocol = window.location.protocol
    const host = window.location.host
    QRCode.toDataURL(`${protocol}//${host}/admin/remote/${eventId}`)
      .then((url) => setQrCodeData(url))
      .catch(() => {})
  }, [eventId])

  const connect = useCallback(() => {
    if (!eventId) return
    if (wsRef.current) wsRef.current.close()

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
    const ws = new WebSocket(`${protocol}//${window.location.host}?eventId=${eventId}`)
    
    ws.onopen = () => setConnected(true)
    ws.onclose = () => setConnected(false)
    ws.onmessage = async (e) => {
      try {
        let text = ''
        if (e.data instanceof Blob) {
          text = await e.data.text()
        } else {
          text = e.data
        }
        const message = JSON.parse(text)
        if (message.type === 'BOOTH_STATUS') {
          setActiveBooths(prev => ({
            ...prev,
            [message.deviceId]: {
              screen: message.screen,
              currentPose: message.currentPose,
              mode: message.mode,
              timestamp: message.timestamp
            }
          }))
        }
      } catch (err) {
        console.error('Failed to parse WS message in remote control', err)
      }
    }
    wsRef.current = ws
  }, [eventId])

  const saveEvent = useEventStore((s) => s.saveEvent)

  const updateEventConfig = (updates: Partial<EventConfig>) => {
    if (!event) return
    const updated = { ...event, ...updates, updatedAt: new Date().toISOString() }
    useEventStore.getState().updateEvent(event.id, updates)
    saveEvent(updated).catch(console.error)
  }

  const currentSuggestions = event?.capture.poseSuggestions || { enabled: true, showSecondsBefore: 2, mode: 'random', poses: [] }
  const enabled = currentSuggestions.enabled ?? true
  const showSecondsBefore = currentSuggestions.showSecondsBefore ?? 2
  const poseMode = currentSuggestions.mode || 'random'
  const localPoses = currentSuggestions.poses || []

  // Merging logic
  const globalFilesSet = new Set(globalPoses.map(p => p.filename))
  const localFilesSet = new Set(localPoses.map(p => p.filename))
  const mergedPoses: Array<{ filename: string; enabled: boolean }> = []

  localPoses.forEach(p => {
    if (globalFilesSet.has(p.filename)) {
      mergedPoses.push({ filename: p.filename, enabled: p.enabled })
    }
  })
  globalPoses.forEach(g => {
    if (globalFilesSet.has(g.filename) && !localFilesSet.has(g.filename)) {
      mergedPoses.push({ filename: g.filename, enabled: g.enabled })
    }
  })

  const poses = mergedPoses.filter(p => p.enabled).map(p => `/poses/${p.filename}`)

  const updatePoseSettings = (updates: {
    enabled?: boolean
    showSecondsBefore?: number
    mode?: 'random' | 'ordered'
    poses?: Array<{ filename: string; enabled: boolean }>
  }) => {
    if (!event) return
    updateEventConfig({
      capture: {
        ...event.capture,
        poseSuggestions: {
          enabled: updates.enabled !== undefined ? updates.enabled : enabled,
          showSecondsBefore: updates.showSecondsBefore !== undefined ? updates.showSecondsBefore : showSecondsBefore,
          mode: updates.mode !== undefined ? updates.mode : poseMode,
          poses: updates.poses !== undefined ? updates.poses : mergedPoses
        }
      }
    })
  }

  const handleTogglePose = (filename: string) => {
    const updated = mergedPoses.map(p => p.filename === filename ? { ...p, enabled: !p.enabled } : p)
    updatePoseSettings({ poses: updated })
  }

  const handleSelectAll = () => {
    const updated = mergedPoses.map(p => ({ ...p, enabled: true }))
    updatePoseSettings({ poses: updated })
  }

  const handleDeselectAll = () => {
    const updated = mergedPoses.map(p => ({ ...p, enabled: false }))
    updatePoseSettings({ poses: updated })
  }

  const handleMovePose = (index: number, direction: 'up' | 'down') => {
    if (direction === 'up' && index === 0) return
    if (direction === 'down' && index === mergedPoses.length - 1) return

    const targetIndex = direction === 'up' ? index - 1 : index + 1
    const updated = [...mergedPoses]
    const temp = updated[index]
    updated[index] = updated[targetIndex]
    updated[targetIndex] = temp
    updatePoseSettings({ poses: updated })
  }

  const handleDeletePose = async (filename: string) => {
    if (!confirm('Are you sure you want to delete this pose suggestion globally? This will remove the file from the server.')) return
    try {
      const res = await fetch(`/api/poses/${encodeURIComponent(filename)}`, { method: 'DELETE' })
      if (res.ok) {
        setGlobalPoses(prev => prev.filter(p => p.filename !== filename))
        const updated = mergedPoses.filter(p => p.filename !== filename)
        updatePoseSettings({ poses: updated })
      }
    } catch (e) {
      console.error(e)
    }
  }

  const handleUploadPose = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setUploading(true)
    const formData = new FormData()
    formData.append('file', file)
    try {
      const res = await fetch('/api/poses/upload', {
        method: 'POST',
        body: formData
      })
      if (res.ok) {
        const data = await res.json()
        const newFilename = data.url.split('/').pop()
        
        // Refresh global poses list from server
        const r = await fetch('/api/poses/settings')
        const settingsData = await r.json()
        setGlobalPoses(settingsData.poses || [])
        
        // Add the new uploaded pose to the event config
        const updated = [...mergedPoses]
        if (!updated.some(p => p.filename === newFilename)) {
          updated.push({ filename: newFilename, enabled: true })
        }
        updatePoseSettings({ poses: updated })
      }
    } catch (e) {
      console.error(e)
    } finally {
      setUploading(false)
      e.target.value = ''
    }
  }

  useEffect(() => {
    // Fetch global poses
    fetch('/api/poses/settings')
      .then((r) => r.json())
      .then((data) => setGlobalPoses(data.poses || []))
      .catch(console.error)

    connect()

    const interval = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.CLOSED) {
        console.log('WS closed, reconnecting...')
        connect()
      }
      
      setActiveBooths(prev => {
        const now = Date.now()
        let changed = false
        const next = { ...prev }
        for (const [id, data] of Object.entries(next)) {
          if (now - data.timestamp > 15000) {
            delete next[id]
            changed = true
          }
        }
        return changed ? next : prev
      })
    }, 5000)

    return () => {
      if (wsRef.current) wsRef.current.close()
      clearInterval(interval)
    }
  }, [eventId, connect])

  const sendCommand = (type: string, data = {}) => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return
    wsRef.current.send(JSON.stringify({ type, ...data }))
  }

  const togglePose = (url: string) => {
    setSelectedPoses((prev) => {
      if (prev.includes(url)) {
        return prev.filter((p) => p !== url)
      }
      if (prev.length >= 3) return prev // Max 3
      return [...prev, url]
    })
  }

  const rigPoses = () => {
    if (selectedPoses.length !== 3) return
    sendCommand('RIG_POSES', { poses: selectedPoses })
    alert('Poses rigged for the next session!')
    setSelectedPoses([])
  }

  if (!event) return <div className="p-4">Event not found</div>

  return (
    <div className="max-w-lg mx-auto p-4 space-y-6 bg-background min-h-screen text-foreground">
      <div className="flex items-center justify-between border-b pb-4">
        <div>
          <h1 className="text-xl font-bold">{event.name}</h1>
          <p className="text-sm text-muted-foreground">Mobile Remote</p>
        </div>
        <div className="flex items-center gap-3">
          <div className={cn("flex items-center gap-1.5 text-sm font-medium", connected ? "text-green-500" : "text-red-500")}>
            {connected ? <Wifi size={16} /> : <WifiOff size={16} />}
            {connected ? "Connected" : "Disconnected"}
          </div>
          <button 
            onClick={connect}
            className="p-1.5 rounded-full hover:bg-muted transition-colors"
            title="Reconnect"
          >
            <RefreshCw size={14} className="text-muted-foreground" />
          </button>
        </div>
      </div>

      {/* QR Code for easy access */}
      {qrCodeData && (
        <div className="bg-card rounded-2xl p-4 shadow-sm border hidden md:flex flex-col items-center gap-2">
          <p className="text-sm font-medium text-muted-foreground">Scan with phone to open remote</p>
          <div className="bg-white p-2 rounded-lg">
            <img src={qrCodeData} alt="QR Code" className="w-32 h-32" />
          </div>
        </div>
      )}

      {/* Quick Controls */}
      <div className="grid grid-cols-2 gap-4">
        <button
          onClick={() => sendCommand('START_COUNTDOWN', { mode: 'photo' })}
          className="flex flex-col items-center justify-center p-6 bg-primary text-primary-foreground rounded-2xl shadow-lg hover:bg-primary/90 transition-colors"
          disabled={!connected}
        >
          <Play size={32} className="mb-2" />
          <span className="font-bold">Start Photo</span>
        </button>
        <button
          onClick={() => sendCommand('STOP_COUNTDOWN')}
          className="flex flex-col items-center justify-center p-6 bg-destructive text-destructive-foreground rounded-2xl shadow-lg hover:bg-destructive/90 transition-colors"
          disabled={!connected}
        >
          <Square size={32} className="mb-2" />
          <span className="font-bold">Stop</span>
        </button>
        <button
          onClick={() => sendCommand('EXIT_FULLSCREEN')}
          className="col-span-2 flex items-center justify-center gap-2 p-4 bg-secondary text-secondary-foreground rounded-2xl font-medium border border-border/50 hover:bg-secondary/80 transition-colors"
          disabled={!connected}
        >
          <span>Exit Full Screen</span>
        </button>
      </div>

      {/* Active Booths */}
      <div className="bg-card rounded-2xl p-4 shadow-sm border space-y-4">
        <div className="flex items-center gap-2 font-bold mb-2">
          <Monitor size={18} />
          <h2>Active Booths</h2>
        </div>
        {Object.entries(activeBooths).length === 0 ? (
          <p className="text-sm text-muted-foreground italic text-center py-4">No active booths detected.</p>
        ) : (
          <div className="space-y-3">
            {Object.entries(activeBooths).map(([id, status]) => (
              <div key={id} className="flex items-center justify-between bg-muted/30 p-3 rounded-xl border border-border/50">
                <div className="space-y-1">
                  <div className="text-sm font-semibold flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.6)]"></div>
                    Booth {id.substring(0, 4)}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Screen: <span className="font-mono bg-muted px-1 py-0.5 rounded text-foreground">{status.screen}</span>
                    {status.screen === 'capture' && ` • Pose: ${status.currentPose}`}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => sendCommand('RELOAD_KIOSK', { target: id })} className="p-2 bg-secondary hover:bg-secondary/80 rounded-lg text-secondary-foreground" title="Reload Page">
                    <RefreshCw size={14} />
                  </button>
                  <button onClick={() => sendCommand('RESTART_EVENT', { target: id })} className="p-2 bg-destructive/10 hover:bg-destructive/20 text-destructive rounded-lg border border-destructive/20" title="Restart to Welcome Screen">
                    <Square size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Settings Button */}
      <button
        onClick={() => setShowSettings(!showSettings)}
        className="w-full flex items-center justify-center gap-2 p-3 bg-secondary text-secondary-foreground rounded-xl font-medium border border-border/50 hover:bg-secondary/80 transition-colors"
      >
        <Settings size={18} />
        {showSettings ? "Hide Settings" : "Event Settings"}
      </button>

      {/* Settings Section */}
      {showSettings && (
        <div className="bg-card rounded-2xl p-4 shadow-sm border space-y-6">
          {/* Camera */}
          <div className="space-y-4">
            <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Camera</h3>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Debug Overlay</span>
              <Switch checked={settings.debugOverlayResolution || false} onCheckedChange={(v) => updateSetting('debugOverlayResolution', v)} />
            </div>
          </div>

          {/* Effects */}
          <div className="space-y-4">
            <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Effects</h3>

          </div>

          {/* Capture */}
          <div className="space-y-4">
            <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Capture</h3>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Pose Suggestions</span>
              <Switch checked={settings.poseSuggestions || false} onCheckedChange={(v) => updateSetting('poseSuggestions', v)} />
            </div>
            <div className="space-y-1">
              <div className="flex items-center justify-between text-sm">
                <span className="text-sm font-medium">Suggestion Display Time</span>
                <span className="font-bold text-primary">{settings.poseSuggestionDelay}s</span>
              </div>
              <input type="range" min="0" max="16" value={settings.poseSuggestionDelay || 0} onChange={(e) => updateSetting('poseSuggestionDelay', parseInt(e.target.value))} className="w-full" />
            </div>

            <div className="space-y-1">
              <div className="flex items-center justify-between text-sm">
                <span className="text-sm font-medium">Countdown Duration</span>
                <span className="font-bold text-primary">{settings.countdownDelay}s</span>
              </div>
              <input type="range" min="1" max="20" value={settings.countdownDelay || 1} onChange={(e) => updateSetting('countdownDelay', parseInt(e.target.value))} className="w-full" />
            </div>
            <div className="space-y-1">
              <div className="flex items-center justify-between text-sm">
                <span className="text-sm font-medium">Intermediate Preview Time</span>
                <span className="font-bold text-primary">{settings.reviewTime}s</span>
              </div>
              <input type="range" min="0" max="60" value={settings.reviewTime || 0} onChange={(e) => updateSetting('reviewTime', parseInt(e.target.value))} className="w-full" />
            </div>
            <div className="space-y-1">
              <div className="flex items-center justify-between text-sm">
                <span className="text-sm font-medium">End Screen Timeout</span>
                <span className="font-bold text-primary">{settings.finishScreenTimeout}s</span>
              </div>
              <input type="range" min="5" max="120" value={settings.finishScreenTimeout || 20} onChange={(e) => updateSetting('finishScreenTimeout', parseInt(e.target.value))} className="w-full" />
            </div>
          </div>

          {/* Screensaver */}
          <div className="space-y-4">
            <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Screensaver</h3>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Enable Attract Loop</span>
              <Switch checked={settings.screensaverEnabled || false} onCheckedChange={(v) => updateSetting('screensaverEnabled', v)} />
            </div>
            <div className="space-y-1">
              <div className="flex items-center justify-between text-sm">
                <span className="text-sm font-medium">Max Messages</span>
                <span className="font-bold text-primary">{settings.maxMessages || 4}</span>
              </div>
              <input type="range" min="1" max="10" value={settings.maxMessages || 4} onChange={(e) => updateSetting('maxMessages', parseInt(e.target.value))} className="w-full" />
            </div>
            <div className="space-y-1">
              <div className="flex items-center justify-between text-sm">
                <span className="text-sm font-medium">Interval Between Messages</span>
                <span className="font-bold text-primary">{settings.intervalSeconds || 2.5}s</span>
              </div>
              <input type="range" min="0.5" max="10" step="0.5" value={settings.intervalSeconds || 2.5} onChange={(e) => updateSetting('intervalSeconds', parseFloat(e.target.value))} className="w-full" />
            </div>
            <div className="space-y-1">
              <div className="flex items-center justify-between text-sm">
                <span className="text-sm font-medium">Motion Sensitivity</span>
                <span className="font-bold text-primary">{settings.motionSensitivity || 50}%</span>
              </div>
              <input type="range" min="1" max="100" value={settings.motionSensitivity || 50} onChange={(e) => updateSetting('motionSensitivity', parseInt(e.target.value))} className="w-full" />
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Wake Method</span>
              <select
                value={settings.wakeMethod || 'touch'}
                onChange={(e) => updateSetting('wakeMethod', e.target.value)}
                className="rounded-lg border border-border bg-background px-2 py-1 text-sm focus:outline-none focus:border-primary"
              >
                <option value="touch">Touch</option>
                <option value="motion">Motion</option>
              </select>
            </div>
          </div>

          {/* Sharing */}
          <div className="space-y-4">
            <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Sharing</h3>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">QR Code</span>
              <Switch checked={settings.qrEnabled || false} onCheckedChange={(v) => updateSetting('qrEnabled', v)} />
            </div>
          </div>

          {/* Zigbee Light */}
          <div className="space-y-4 pt-4 border-t border-border/50">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-medium">Use Photo Light</div>
                <div className="text-xs text-muted-foreground">Turn on Zigbee light during capture</div>
              </div>
              <Switch checked={settings.zigbeeLightEnabled || false} onCheckedChange={(v) => updateSetting('zigbeeLightEnabled', v)} />
            </div>
            
            {(settings.zigbeeLightEnabled || false) && (
              <div className="flex items-center justify-between bg-muted/40 p-2.5 rounded-lg border border-border/50">
                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Manual Flashlight</span>
                <div className="flex gap-2">
                  <button
                    onClick={() => sendCommand('TRIGGER_ZIGBEE_LIGHT', { state: 'OFF' })}
                    className="px-3 py-1 bg-secondary text-secondary-foreground hover:bg-secondary/80 active:scale-95 rounded-lg text-xs font-semibold transition-all border border-border/50"
                  >
                    OFF
                  </button>
                  <button
                    onClick={() => sendCommand('TRIGGER_ZIGBEE_LIGHT', { state: 'ON' })}
                    className="px-3 py-1 bg-primary text-primary-foreground hover:bg-primary/95 active:scale-95 rounded-lg text-xs font-semibold transition-all shadow-sm"
                  >
                    ON
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Reset to Defaults */}
          <div className="pt-4 border-t border-border/50">
            <button
              onClick={resetToDefaults}
              className="w-full p-2.5 bg-secondary text-secondary-foreground rounded-lg text-sm font-medium hover:bg-secondary/80 transition-colors"
            >
              Reset to Defaults
            </button>
          </div>
        </div>
      )}

      {/* Quick Settings */}
      <div className="bg-card rounded-2xl p-4 shadow-sm border space-y-4">
        <div className="flex items-center gap-2 font-bold mb-2">
          <Settings size={18} />
          <h2>Quick Settings</h2>
        </div>
        
        <div className="flex items-center justify-between">
          <span>Record Session Video</span>
          <label className="relative inline-flex items-center cursor-pointer">
            <input type="checkbox" className="hidden peer" checked={settings.recordSessionVideo || false} onChange={(e) => {
              updateSetting('recordSessionVideo', e.target.checked)
              sendCommand('UPDATE_SETTINGS', { settings: { recordSessionVideo: e.target.checked } })
            }} />
            <div className="w-11 h-6 bg-muted peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
          </label>
        </div>

        <div className="flex items-center justify-between">
          <span>Show Gallery Button</span>
          <label className="relative inline-flex items-center cursor-pointer">
            <input type="checkbox" className="hidden peer" checked={settings.galleryEnabled !== false} onChange={(e) => {
              updateSetting('galleryEnabled', e.target.checked)
              sendCommand('UPDATE_SETTINGS', { settings: { galleryEnabled: e.target.checked } })
            }} />
            <div className="w-11 h-6 bg-muted peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
          </label>
        </div>

        <div className="flex items-center justify-between">
          <span>Start Button Image</span>
          <label className="relative inline-flex items-center cursor-pointer">
            <input type="checkbox" className="hidden peer" checked={settings.startButtonImageEnabled || false} onChange={(e) => {
              updateSetting('startButtonImageEnabled', e.target.checked)
              sendCommand('UPDATE_SETTINGS', { settings: { startButtonImageEnabled: e.target.checked } })
            }} />
            <div className="w-11 h-6 bg-muted peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
          </label>
        </div>

        <div className="flex items-center justify-between">
          <span>Maintenance Mode</span>
          <label className="relative inline-flex items-center cursor-pointer">
            <input type="checkbox" className="hidden peer" checked={settings.maintenanceMode || false} onChange={(e) => {
              updateSetting('maintenanceMode', e.target.checked)
              sendCommand('UPDATE_SETTINGS', { settings: { maintenanceMode: e.target.checked } })
            }} />
            <div className="w-11 h-6 bg-muted peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
          </label>
        </div>

        <div className="space-y-1">
          <label className="text-sm font-medium">Maintenance Title</label>
          <input 
            type="text" 
            value={settings.maintenanceTitle ?? 'Under Maintenance'} 
            onChange={(e) => {
              updateSetting('maintenanceTitle', e.target.value)
              sendCommand('UPDATE_SETTINGS', { settings: { maintenanceTitle: e.target.value } })
            }} 
            className="w-full p-2 border rounded-md bg-background text-foreground"
            placeholder="Enter custom title..."
          />
        </div>

        <div className="space-y-1">
          <label className="text-sm font-medium">Maintenance Message</label>
          <input 
            type="text" 
            value={settings.maintenanceText ?? 'Event currently in maintenance'} 
            onChange={(e) => {
              updateSetting('maintenanceText', e.target.value)
              sendCommand('UPDATE_SETTINGS', { settings: { maintenanceText: e.target.value } })
            }} 
            className="w-full p-2 border rounded-md bg-background text-foreground"
            placeholder="Enter custom message..."
          />
        </div>

        <div className="flex items-center justify-between">
          <span>Face Tracking AI (Beta)</span>
          <label className="relative inline-flex items-center cursor-pointer">
            <input type="checkbox" className="hidden peer" checked={settings.advancedBeautyFilter || false} onChange={(e) => {
              updateSetting('advancedBeautyFilter', e.target.checked)
              sendCommand('UPDATE_SETTINGS', { settings: { advancedBeautyFilter: e.target.checked } })
            }} />
            <div className="w-11 h-6 bg-muted peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
          </label>
        </div>
        {settings.advancedBeautyFilter && (
          <div className="space-y-2">
            <div className="space-y-1">
              <label className="text-sm font-medium">Select Prop</label>
              <select 
                value={settings.faceTrackingProp || ''} 
                onChange={(e) => {
                  const val = e.target.value
                  updateSetting('faceTrackingProp', val)
                  sendCommand('UPDATE_SETTINGS', { settings: { faceTrackingProp: val } })
                }} 
                className="w-full p-2 border rounded-md bg-background text-foreground"
              >
                <option value="">None</option>
                {propsList.map(prop => (
                  <option key={prop} value={`/props/${prop}`}>{prop}</option>
                ))}
                <option value="custom">Custom URL...</option>
              </select>
            </div>
            
            {(settings.faceTrackingProp === 'custom' || (settings.faceTrackingProp && !settings.faceTrackingProp.startsWith('/props/'))) && (
              <div className="space-y-1">
                <label className="text-sm font-medium">Custom Prop URL</label>
                <input 
                  type="text" 
                  value={settings.faceTrackingProp === 'custom' ? '' : settings.faceTrackingProp} 
                  onChange={(e) => {
                    updateSetting('faceTrackingProp', e.target.value)
                    sendCommand('UPDATE_SETTINGS', { settings: { faceTrackingProp: e.target.value } })
                  }} 
                  onPaste={(e) => {
                    const items = e.clipboardData.items
                    for (let i = 0; i < items.length; i++) {
                      if (items[i].type.indexOf('image') !== -1) {
                        const blob = items[i].getAsFile()
                        if (blob) {
                          const reader = new FileReader()
                          reader.onload = (event) => {
                            const base64 = event.target?.result as string
                            updateSetting('faceTrackingProp', base64)
                            sendCommand('UPDATE_SETTINGS', { settings: { faceTrackingProp: base64 } })
                          }
                          reader.readAsDataURL(blob)
                          e.preventDefault()
                          break
                        }
                      }
                    }
                  }}
                  className="w-full p-2 border rounded-md bg-background text-foreground"
                  placeholder="Paste URL or Image"
                />
              </div>
            )}
          </div>
        )}
      </div>

      {/* Pose Suggestions Manager */}
      <div className="bg-card rounded-2xl p-4 shadow-sm border space-y-4">
        <div className="flex items-center gap-2 font-bold">
          <Sparkles size={18} className="text-primary animate-pulse" />
          <h2>Pose Manager</h2>
        </div>

        {/* Enable / Disable */}
        <label className="flex items-center justify-between p-3 bg-muted/30 border border-border/50 rounded-xl cursor-pointer hover:bg-muted/50 transition-colors">
          <div className="space-y-0.5">
            <span className="text-xs font-semibold text-foreground">Enable Pose Suggestions</span>
            <p className="text-[10px] text-muted-foreground">Show pose images to guests during countdowns</p>
          </div>
          <Switch
            checked={enabled}
            onCheckedChange={(val) => {
              updatePoseSettings({ enabled: val })
              updateSetting('poseSuggestions', val)
            }}
          />
        </label>

        {enabled && (
          <>
            {/* Countdown Delay slider */}
            <div className="space-y-1">
              <div className="flex justify-between items-center text-xs">
                <span className="font-semibold text-muted-foreground">Pose Intro Duration</span>
                <span className="font-mono text-xs bg-muted px-1.5 py-0.5 rounded text-foreground">{showSecondsBefore}s</span>
              </div>
              <input
                type="range"
                min={0}
                max={20}
                value={showSecondsBefore}
                onChange={(e) => {
                  const val = Number(e.target.value)
                  updatePoseSettings({ showSecondsBefore: val })
                  updateSetting('poseSuggestionDelay', val)
                }}
                className="w-full h-1.5 bg-muted rounded-lg appearance-none cursor-pointer accent-primary"
              />
            </div>

            {/* Mode selection */}
            <div className="space-y-2">
              <span className="text-xs font-semibold text-muted-foreground">Sequence Mode</span>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => updatePoseSettings({ mode: 'random' })}
                  className={`flex flex-col items-start p-2.5 rounded-lg border text-left transition-all ${
                    poseMode === 'random'
                      ? 'border-primary bg-primary/5 text-foreground ring-1 ring-primary'
                      : 'border-border bg-background text-muted-foreground hover:bg-muted/30'
                  }`}
                >
                  <span className="font-semibold text-xs text-foreground">Randomized</span>
                  <span className="text-[9px] mt-0.5 text-muted-foreground">Shuffle every session</span>
                </button>

                <button
                  type="button"
                  onClick={() => updatePoseSettings({ mode: 'ordered' })}
                  className={`flex flex-col items-start p-2.5 rounded-lg border text-left transition-all ${
                    poseMode === 'ordered'
                      ? 'border-primary bg-primary/5 text-foreground ring-1 ring-primary'
                      : 'border-border bg-background text-muted-foreground hover:bg-muted/30'
                  }`}
                >
                  <span className="font-semibold text-xs text-foreground">Specific</span>
                  <span className="text-[9px] mt-0.5 text-muted-foreground">Use list order below</span>
                </button>
              </div>
            </div>

            {/* Pose Library Grid */}
            <div className="space-y-3 pt-2">
              <div className="flex justify-between items-center">
                <span className="text-xs font-semibold text-muted-foreground">Pose Library</span>
                
                <label className={`cursor-pointer rounded-lg border border-border bg-secondary px-2.5 py-1.5 text-[10px] font-semibold hover:bg-secondary/80 transition-colors flex items-center gap-1.5 ${uploading ? 'opacity-50 pointer-events-none' : ''}`}>
                  <Upload className="h-3 w-3" />
                  {uploading ? 'Uploading...' : 'Upload PNG'}
                  <input type="file" accept="image/png" className="hidden" onChange={handleUploadPose} disabled={uploading} />
                </label>
              </div>

              {mergedPoses.length > 0 && (
                <div className="flex items-center gap-2 text-[10px]">
                  <button type="button" onClick={handleSelectAll} className="font-semibold text-primary hover:underline">Select All</button>
                  <span className="text-muted-foreground/30">|</span>
                  <button type="button" onClick={handleDeselectAll} className="font-semibold text-muted-foreground hover:text-foreground">Deselect All</button>
                </div>
              )}

              {mergedPoses.length === 0 ? (
                <p className="text-xs text-muted-foreground italic text-center py-4">No poses uploaded yet.</p>
              ) : (
                <div className="grid grid-cols-3 gap-2">
                  {mergedPoses.map((p, idx) => (
                    <div
                      key={p.filename}
                      className={`group relative flex flex-col rounded-lg border transition-all p-2 bg-card ${
                        p.enabled
                          ? 'border-border/80 hover:border-primary/50'
                          : 'border-border/40 opacity-55 hover:opacity-85'
                      }`}
                    >
                      {/* Image Preview */}
                      <div className="relative aspect-[3/4] w-full rounded bg-muted/30 overflow-hidden flex items-center justify-center p-1">
                        <img src={`/poses/${p.filename}`} className="h-full w-full object-contain" alt="" />
                        
                        {/* Delete button */}
                        <div className="absolute top-1 right-1">
                          <button
                            type="button"
                            onClick={() => handleDeletePose(p.filename)}
                            className="rounded bg-destructive/10 p-1 text-destructive hover:bg-destructive/20 border border-destructive/20"
                            title="Delete Suggestion Globally"
                          >
                            <Trash2 className="h-3 w-3" />
                          </button>
                        </div>
                      </div>

                      {/* Info & Checkbox */}
                      <div className="mt-2 flex items-center gap-1.5">
                        <input
                          type="checkbox"
                          checked={p.enabled}
                          onChange={() => handleTogglePose(p.filename)}
                          className="h-3.5 w-3.5 rounded border-border text-primary focus:ring-primary cursor-pointer bg-background"
                          id={`rc-pose-chk-${p.filename}`}
                        />
                        <label 
                          htmlFor={`rc-pose-chk-${p.filename}`}
                          className="text-[9px] font-medium truncate flex-1 cursor-pointer select-none text-foreground/90"
                          title={p.filename}
                        >
                          {p.filename.replace(/\.[^/.]+$/, "")}
                        </label>
                      </div>

                      {/* Move Poses */}
                      {poseMode === 'ordered' && (
                        <div className="mt-2 pt-1.5 border-t border-border/40 flex justify-between gap-1">
                          <button
                            type="button"
                            onClick={() => handleMovePose(idx, 'up')}
                            disabled={idx === 0}
                            className="flex-1 flex items-center justify-center py-1 rounded bg-secondary hover:bg-secondary/80 text-[8px] font-bold text-foreground/80 disabled:opacity-30 disabled:pointer-events-none transition-colors"
                            title="Move Up"
                          >
                            <ArrowUp className="h-2.5 w-2.5" />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleMovePose(idx, 'down')}
                            disabled={idx === mergedPoses.length - 1}
                            className="flex-1 flex items-center justify-center py-1 rounded bg-secondary hover:bg-secondary/80 text-[8px] font-bold text-foreground/80 disabled:opacity-30 disabled:pointer-events-none transition-colors"
                            title="Move Down"
                          >
                            <ArrowDown className="h-2.5 w-2.5" />
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Pose Rigging */}
      <div className="bg-card rounded-2xl p-4 shadow-sm border space-y-4">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2 font-bold">
            <Grid size={18} />
            <h2>Rig Poses ({selectedPoses.length}/3)</h2>
          </div>
          <button
            onClick={rigPoses}
            disabled={selectedPoses.length !== 3 || !connected}
            className="text-xs bg-primary text-primary-foreground px-3 py-1.5 rounded-full font-medium disabled:opacity-50"
          >
            Apply
          </button>
        </div>
        
        <p className="text-xs text-muted-foreground">Select exactly 3 poses to force for the NEXT session.</p>

        {poses.length === 0 ? (
          <p className="text-xs text-muted-foreground italic text-center py-4">No enabled poses available to rig. Enable some above.</p>
        ) : (
          <div className="grid grid-cols-3 gap-2">
            {poses.map((url) => {
              const isSelected = selectedPoses.includes(url)
              return (
                <div
                  key={url}
                  onClick={() => togglePose(url)}
                  className={cn(
                    "relative aspect-square rounded-lg overflow-hidden border-2 cursor-pointer transition-all",
                    isSelected ? "border-primary shadow-md" : "border-transparent bg-muted/50"
                  )}
                >
                  <img src={url} alt="" className="w-full h-full object-contain p-1" />
                  {isSelected && (
                    <div className="absolute inset-0 bg-primary/10 flex items-center justify-center">
                      <div className="bg-primary text-primary-foreground rounded-full p-0.5">
                        <Check size={12} />
                      </div>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}
